import { cloneVNode, defineComponent, h, isVNode, mergeProps, Text, } from 'vue';
import { useWefatHoverState } from './hover-state.js';
export const WEFAT_COMPONENT_DOM_VERSION = 1;
const WEFAT_TEXT_ENTITY_REPLACEMENTS = Object.freeze([
    Object.freeze({ entity: '&nbsp;', value: '\u00a0' }),
    Object.freeze({ entity: '&lt;', value: '<' }),
    Object.freeze({ entity: '&gt;', value: '>' }),
    Object.freeze({ entity: '&amp;', value: '&' }),
    Object.freeze({ entity: '&apos;', value: "'" }),
    Object.freeze({ entity: '&ensp;', value: '\u2002' }),
    Object.freeze({ entity: '&emsp;', value: '\u2003' }),
]);
export function decodeWefatTextEntities(value) {
    let result = '';
    for (let index = 0; index < value.length;) {
        let replacement;
        if (value[index] === '&') {
            replacement = WEFAT_TEXT_ENTITY_REPLACEMENTS.find((item) => (value.startsWith(item.entity, index)));
        }
        if (replacement === undefined) {
            result += value[index];
            index += 1;
        }
        else {
            result += replacement.value;
            index += replacement.entity.length;
        }
    }
    return result;
}
function decodeWefatTextVNode(child) {
    if (child.type === Text && typeof child.children === 'string') {
        const decoded = cloneVNode(child);
        decoded.children = decodeWefatTextEntities(child.children);
        return decoded;
    }
    return child;
}
function decodeWefatTextChildren(children) {
    return children?.map((child) => isVNode(child) ? decodeWefatTextVNode(child) : child);
}
// Built-in defaults that should yield to source WXSS use zero-specificity selectors.
export const WEFAT_BASE_COMPONENT_STYLES = `
[data-wefat-app] .wefat-view,
:where([data-wefat-app] .wefat-text),
:where([data-wefat-app] .wefat-image),
:where([data-wefat-app] .wefat-button),
[data-wefat-app] .wefat-input,
[data-wefat-app] .wefat-scroll-view,
[data-wefat-app] .wefat-switch,
[data-wefat-app] .wefat-swiper,
[data-wefat-app] .wefat-swiper-item,
[data-wefat-app] .wefat-textarea,
[data-wefat-app] .wefat-web-view {
  box-sizing: border-box;
}

:where([data-wefat-app] .wefat-view) {
  display: block;
}

[data-wefat-app] .wefat-scroll-view--flex {
  display: flex;
}

[data-wefat-app] .wefat-scroll-view--hidden-scrollbar {
  scrollbar-width: none;
}

[data-wefat-app] .wefat-scroll-view--hidden-scrollbar::-webkit-scrollbar {
  display: none;
}

[data-wefat-app] .wefat-swiper {
  display: block;
  overflow: hidden;
  position: relative;
  touch-action: pan-y;
}

[data-wefat-app] .wefat-swiper--vertical {
  touch-action: pan-x;
}

[data-wefat-app] .wefat-swiper__track {
  display: flex;
  height: 100%;
  transition-property: transform;
  transition-timing-function: ease;
  width: 100%;
}

[data-wefat-app] .wefat-swiper-item {
  display: block;
  flex: 0 0 100%;
  height: 100%;
  min-width: 0;
  overflow: hidden;
}

[data-wefat-app] .wefat-swiper__indicators {
  bottom: 8px;
  display: flex;
  gap: 6px;
  left: 50%;
  position: absolute;
  transform: translateX(-50%);
}

[data-wefat-app] .wefat-swiper__indicator {
  border-radius: 50%;
  display: block;
  height: 6px;
  width: 6px;
}

:where([data-wefat-app] .wefat-text) {
  display: inline;
}

:where([data-wefat-app] .wefat-image) {
  display: inline-block;
  width: 320px;
  height: 240px;
  vertical-align: top;
}

:where([data-wefat-app] .wefat-button) {
  position: relative;
  display: inline-flex;
  min-height: 44px;
  align-items: center;
  justify-content: center;
  gap: 8px;
  margin: 0;
  padding: 8px 20px;
  border: 0;
  border-radius: 4px;
  background: #f8fafc;
  color: #111827;
  font: inherit;
  line-height: 1.4;
  cursor: pointer;
}

:where([data-wefat-app] .wefat-button::after) {
  position: absolute;
  inset: 0;
  box-sizing: border-box;
  border: 1px solid #d1d5db;
  border-radius: inherit;
  content: "";
  pointer-events: none;
}

:where([data-wefat-app] .wefat-button:focus-visible) {
  outline: 3px solid #2563eb;
  outline-offset: 2px;
}

:where([data-wefat-app] .wefat-button--primary) {
  background: #07a957;
  color: #ffffff;
}

:where([data-wefat-app] .wefat-button--primary::after) {
  border-color: #078a46;
}

:where([data-wefat-app] .wefat-button--warn) {
  background: #dc3b3b;
  color: #ffffff;
}

:where([data-wefat-app] .wefat-button--warn::after) {
  border-color: #c53030;
}

:where([data-wefat-app] .wefat-button--mini) {
  min-height: 32px;
  padding: 4px 12px;
  font-size: 13px;
}

:where([data-wefat-app] .wefat-button--plain) {
  background: transparent;
  color: inherit;
}

:where([data-wefat-app] .wefat-button--primary.wefat-button--plain) {
  color: #078a46;
}

:where([data-wefat-app] .wefat-button--warn.wefat-button--plain) {
  color: #c53030;
}

:where([data-wefat-app] .wefat-button:disabled) {
  opacity: 0.5;
  cursor: not-allowed;
}

[data-wefat-app] .wefat-switch {
  --wefat-switch-color: #04be02;
  position: relative;
  display: inline-flex;
  width: 52px;
  height: 32px;
  margin: 0;
  padding: 0;
  vertical-align: middle;
  cursor: pointer;
}

[data-wefat-app] .wefat-switch__input {
  position: absolute;
  z-index: 1;
  inset: 0;
  width: 100%;
  height: 100%;
  margin: 0;
  opacity: 0;
  cursor: inherit;
}

[data-wefat-app] .wefat-switch__control {
  position: relative;
  display: block;
  width: 100%;
  height: 100%;
  border: 1px solid #d1d5db;
  border-radius: 16px;
  background: #d1d5db;
  transition: background-color 160ms ease, border-color 160ms ease;
}

[data-wefat-app] .wefat-switch__control::after {
  position: absolute;
  top: 1px;
  left: 1px;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: #ffffff;
  box-shadow: 0 1px 3px rgb(0 0 0 / 24%);
  content: "";
  transition: transform 160ms ease;
}

[data-wefat-app] .wefat-switch__input:checked + .wefat-switch__control {
  border-color: var(--wefat-switch-color);
  background: var(--wefat-switch-color);
}

[data-wefat-app] .wefat-switch__input:checked + .wefat-switch__control::after {
  transform: translateX(20px);
}

[data-wefat-app] .wefat-switch__input:focus-visible + .wefat-switch__control {
  outline: 3px solid #2563eb;
  outline-offset: 2px;
}

[data-wefat-app] .wefat-switch__input:disabled + .wefat-switch__control {
  opacity: 0.5;
}

[data-wefat-app] .wefat-switch--disabled {
  cursor: not-allowed;
}

[data-wefat-app] .wefat-switch--checkbox {
  width: 32px;
}

[data-wefat-app] .wefat-switch--checkbox .wefat-switch__control {
  border-radius: 4px;
  background: #ffffff;
}

[data-wefat-app] .wefat-switch--checkbox .wefat-switch__control::after {
  top: 6px;
  left: 10px;
  width: 8px;
  height: 14px;
  border: solid #ffffff;
  border-width: 0 3px 3px 0;
  border-radius: 0;
  background: transparent;
  box-shadow: none;
  opacity: 0;
  transform: rotate(45deg);
}

[data-wefat-app] .wefat-switch--checkbox .wefat-switch__input:checked + .wefat-switch__control::after {
  opacity: 1;
  transform: rotate(45deg);
}

[data-wefat-app] .wefat-modal {
  width: min(88vw, 420px);
  max-height: min(80vh, 640px);
  padding: 0;
  overflow: hidden;
  border: 0;
  border-radius: 8px;
  background: #ffffff;
  color: #17202e;
  box-shadow: 0 16px 48px rgb(0 0 0 / 28%);
}

[data-wefat-app] .wefat-modal::backdrop {
  background: rgb(0 0 0 / 48%);
}

[data-wefat-app] .wefat-modal__title {
  margin: 0;
  padding: 24px 24px 0;
  font-size: 18px;
  font-weight: 600;
  line-height: 1.4;
  text-align: center;
}

[data-wefat-app] .wefat-modal__content {
  max-height: 46vh;
  margin: 0;
  padding: 16px 24px 24px;
  overflow: auto;
  color: #4b5563;
  font-size: 15px;
  line-height: 1.6;
  overflow-wrap: anywhere;
  text-align: center;
  white-space: pre-wrap;
}

[data-wefat-app] .wefat-modal__input {
  box-sizing: border-box;
  width: calc(100% - 48px);
  min-height: 44px;
  margin: 0 24px 24px;
  padding: 10px 12px;
  border: 1px solid #cbd5e1;
  border-radius: 4px;
  background: #ffffff;
  color: #17202e;
  font: inherit;
}

[data-wefat-app] .wefat-modal__actions {
  display: grid;
  grid-auto-columns: minmax(0, 1fr);
  grid-auto-flow: column;
  border-top: 1px solid #e5e7eb;
}

[data-wefat-app] .wefat-modal__button {
  min-height: 52px;
  padding: 8px 16px;
  border: 0;
  border-left: 1px solid #e5e7eb;
  background: transparent;
  font: inherit;
  font-weight: 600;
  cursor: pointer;
}

[data-wefat-app] .wefat-modal__button:first-child {
  border-left: 0;
}

[data-wefat-app] .wefat-modal__button:focus-visible,
[data-wefat-app] .wefat-modal__input:focus-visible {
  outline: 3px solid #2563eb;
  outline-offset: -3px;
}

[data-wefat-app] .wefat-action-sheet {
  width: min(92vw, 420px);
  max-height: min(80vh, 680px);
  padding: 0;
  overflow: hidden;
  border: 0;
  border-radius: 8px;
  background: #f3f4f6;
  color: #17202e;
  box-shadow: 0 16px 48px rgb(0 0 0 / 28%);
}

[data-wefat-app] .wefat-action-sheet::backdrop {
  background: rgb(0 0 0 / 48%);
}

[data-wefat-app] .wefat-action-sheet__alert {
  margin: 0;
  padding: 14px 20px;
  border-bottom: 1px solid #e5e7eb;
  background: #ffffff;
  color: #6b7280;
  font-size: 13px;
  line-height: 1.5;
  text-align: center;
}

[data-wefat-app] .wefat-action-sheet__list {
  max-height: min(56vh, 480px);
  overflow: auto;
  background: #ffffff;
}

[data-wefat-app] .wefat-action-sheet__item,
[data-wefat-app] .wefat-action-sheet__cancel {
  display: block;
  width: 100%;
  min-height: 52px;
  padding: 10px 20px;
  border: 0;
  border-top: 1px solid #e5e7eb;
  background: #ffffff;
  font: inherit;
  line-height: 1.4;
  overflow-wrap: anywhere;
  cursor: pointer;
}

[data-wefat-app] .wefat-action-sheet__item:first-child {
  border-top: 0;
}

[data-wefat-app] .wefat-action-sheet__cancel {
  margin-top: 8px;
  font-weight: 600;
}

[data-wefat-app] .wefat-action-sheet__item:focus-visible,
[data-wefat-app] .wefat-action-sheet__cancel:focus-visible {
  outline: 3px solid #2563eb;
  outline-offset: -3px;
}

@media (max-width: 600px) {
  [data-wefat-app] .wefat-action-sheet {
    width: 100%;
    max-width: none;
    margin: auto 0 0;
    border-radius: 8px 8px 0 0;
  }
}

[data-wefat-app] .wefat-image-preview {
  box-sizing: border-box;
  width: 100vw;
  max-width: none;
  height: 100vh;
  height: 100dvh;
  max-height: none;
  margin: 0;
  padding: 0;
  overflow: hidden;
  border: 0;
  background: #08090b;
  color: #ffffff;
}

[data-wefat-app] .wefat-image-preview::backdrop {
  background: rgb(0 0 0 / 88%);
}

[data-wefat-app] .wefat-image-preview__stage {
  display: grid;
  width: 100%;
  height: 100%;
  place-items: center;
}

[data-wefat-app] .wefat-image-preview__image {
  display: block;
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
  opacity: 0;
  transition: opacity 160ms ease;
}

[data-wefat-app] .wefat-image-preview[data-wefat-image-state="ready"] .wefat-image-preview__image {
  opacity: 1;
}

[data-wefat-app] .wefat-image-preview[data-wefat-image-state="error"] .wefat-image-preview__image {
  visibility: hidden;
}

[data-wefat-app] .wefat-image-preview__close,
[data-wefat-app] .wefat-image-preview__previous,
[data-wefat-app] .wefat-image-preview__next {
  position: absolute;
  z-index: 1;
  display: grid;
  padding: 0;
  border: 1px solid rgb(255 255 255 / 28%);
  border-radius: 4px;
  background: rgb(8 9 11 / 78%);
  color: #ffffff;
  font: inherit;
  line-height: 1;
  place-items: center;
  cursor: pointer;
}

[data-wefat-app] .wefat-image-preview__close {
  top: max(12px, env(safe-area-inset-top));
  right: max(12px, env(safe-area-inset-right));
  width: 48px;
  height: 48px;
  font-size: 32px;
}

[data-wefat-app] .wefat-image-preview__previous,
[data-wefat-app] .wefat-image-preview__next {
  top: 50%;
  width: 48px;
  height: 64px;
  font-size: 44px;
  transform: translateY(-50%);
}

[data-wefat-app] .wefat-image-preview__previous {
  left: max(12px, env(safe-area-inset-left));
}

[data-wefat-app] .wefat-image-preview__next {
  right: max(12px, env(safe-area-inset-right));
}

[data-wefat-app] .wefat-image-preview__previous:disabled,
[data-wefat-app] .wefat-image-preview__next:disabled {
  opacity: 0.35;
  cursor: default;
}

[data-wefat-app] .wefat-image-preview__close:focus-visible,
[data-wefat-app] .wefat-image-preview__previous:focus-visible,
[data-wefat-app] .wefat-image-preview__next:focus-visible {
  outline: 3px solid #ffffff;
  outline-offset: 2px;
}

[data-wefat-app] .wefat-image-preview__status,
[data-wefat-app] .wefat-image-preview__count {
  position: absolute;
  left: 50%;
  z-index: 1;
  margin: 0;
  padding: 6px 10px;
  border-radius: 4px;
  background: rgb(8 9 11 / 78%);
  font-size: 14px;
  line-height: 1.4;
  transform: translateX(-50%);
}

[data-wefat-app] .wefat-image-preview__status {
  top: 50%;
  min-width: 130px;
  text-align: center;
  transform: translate(-50%, -50%);
}

[data-wefat-app] .wefat-image-preview__status:empty {
  display: none;
}

[data-wefat-app] .wefat-image-preview__count {
  bottom: max(16px, env(safe-area-inset-bottom));
}

[data-wefat-app] .wefat-input {
  min-width: 0;
  margin: 0;
  padding: 0;
  border: 0;
  border-radius: 0;
  background: transparent;
  color: inherit;
  font: inherit;
  line-height: inherit;
  outline: none;
}

[data-wefat-app] .wefat-textarea {
  display: block;
  width: 300px;
  height: 150px;
  min-width: 0;
  margin: 0;
  padding: 0;
  border: 0;
  border-radius: 0;
  background: transparent;
  color: inherit;
  font: inherit;
  line-height: inherit;
  outline: none;
  resize: none;
}

:where([data-wefat-app] .wefat-rich-text) {
  display: block;
  min-width: 0;
  overflow-wrap: anywhere;
}

:where([data-wefat-app] .wefat-rich-text img) {
  max-width: 100%;
  height: auto;
}

:where([data-wefat-app] .wefat-rich-text blockquote) {
  margin: 1em 0;
  padding: 0.65em 1em;
  border-inline-start: 4px solid color-mix(in srgb, currentColor 32%, transparent);
  background: color-mix(in srgb, currentColor 6%, transparent);
  color: color-mix(in srgb, currentColor 76%, transparent);
}

:where([data-wefat-app] .wefat-rich-text blockquote > :first-child) {
  margin-top: 0;
}

:where([data-wefat-app] .wefat-rich-text blockquote > :last-child) {
  margin-bottom: 0;
}

:where([data-wefat-app] .wefat-rich-text code) {
  padding: 0.15em 0.4em;
  border: 1px solid color-mix(in srgb, currentColor 12%, transparent);
  border-radius: 3px;
  background: color-mix(in srgb, currentColor 8%, transparent);
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", monospace;
  font-size: 0.9em;
  line-height: 1.55;
  overflow-wrap: anywhere;
}

:where([data-wefat-app] .wefat-rich-text pre) {
  max-width: 100%;
  margin: 1em 0;
  padding: 1em 1.1em;
  overflow-x: auto;
  border: 1px solid color-mix(in srgb, currentColor 14%, transparent);
  border-radius: 6px;
  background: color-mix(in srgb, currentColor 8%, transparent);
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", monospace;
  font-size: 0.9em;
  line-height: 1.65;
  tab-size: 4;
  white-space: pre;
  overflow-wrap: normal;
}

:where([data-wefat-app] .wefat-rich-text pre code) {
  padding: 0;
  border: 0;
  border-radius: 0;
  background: transparent;
  color: inherit;
  font: inherit;
  line-height: inherit;
  white-space: inherit;
  overflow-wrap: inherit;
}

[data-wefat-app] .wefat-web-view {
  display: flex;
  width: 100%;
  min-height: 44px;
  align-items: center;
  justify-content: center;
  color: #0b57d0;
  overflow-wrap: anywhere;
  text-decoration: underline;
}

[data-wefat-app] .wefat-web-view[aria-disabled="true"] {
  color: #6b7280;
  text-decoration: none;
}

:where([data-wefat-app] .wefat-button__loading) {
  width: 1em;
  height: 1em;
  border: 2px solid currentColor;
  border-right-color: transparent;
  border-radius: 50%;
  animation: wefat-button-spin 0.8s linear infinite;
}

@keyframes wefat-button-spin {
  to { transform: rotate(360deg); }
}

@media (prefers-reduced-motion: reduce) {
  :where([data-wefat-app] .wefat-button__loading) {
    animation: none;
  }

  [data-wefat-app] .wefat-image-preview__image {
    transition: none;
  }

  [data-wefat-app] .wefat-switch__control,
  [data-wefat-app] .wefat-switch__control::after {
    transition: none;
  }
}

[data-wefat-app] [hidden] {
  display: none !important;
}
`.trim();
export const WefatView = defineComponent({
    name: 'WefatView',
    inheritAttrs: false,
    props: {
        hidden: {
            type: Boolean,
            default: false,
        },
        hoverClass: {
            type: String,
            default: 'none',
        },
        hoverStartTime: {
            type: [Number, String],
            default: 50,
        },
        hoverStayTime: {
            type: [Number, String],
            default: 400,
        },
    },
    setup(props, { attrs, slots }) {
        const hover = useWefatHoverState(props);
        return () => h('div', mergeProps(attrs, {
            class: ['wefat-view', hover.active.value && props.hoverClass !== 'none' ? props.hoverClass : null],
            'data-wefat-component': 'view',
            'data-wefat-component-version': String(WEFAT_COMPONENT_DOM_VERSION),
            hidden: props.hidden ? true : undefined,
            onPointercancel: hover.onPointercancel,
            onPointerdown: hover.onPointerdown,
            onPointerleave: hover.onPointerleave,
            onPointerup: hover.onPointerup,
        }), slots.default?.());
    },
});
export const WefatText = defineComponent({
    name: 'WefatText',
    inheritAttrs: false,
    props: {
        decode: {
            type: Boolean,
            default: false,
        },
        hidden: {
            type: Boolean,
            default: false,
        },
        hoverClass: {
            type: String,
            default: 'none',
        },
        hoverStartTime: {
            type: [Number, String],
            default: 50,
        },
        hoverStayTime: {
            type: [Number, String],
            default: 400,
        },
        selectable: {
            type: Boolean,
            default: false,
        },
        userSelect: {
            type: Boolean,
            default: false,
        },
    },
    setup(props, { attrs, slots }) {
        const hover = useWefatHoverState(props);
        return () => h('span', mergeProps(attrs, {
            class: ['wefat-text', hover.active.value && props.hoverClass !== 'none' ? props.hoverClass : null],
            'data-wefat-component': 'text',
            'data-wefat-component-version': String(WEFAT_COMPONENT_DOM_VERSION),
            hidden: props.hidden ? true : undefined,
            onPointercancel: hover.onPointercancel,
            onPointerdown: hover.onPointerdown,
            onPointerleave: hover.onPointerleave,
            onPointerup: hover.onPointerup,
            style: props.selectable || props.userSelect
                ? { userSelect: 'text', WebkitUserSelect: 'text' }
                : undefined,
        }), props.decode
            ? decodeWefatTextChildren(slots.default?.())
            : slots.default?.());
    },
});
//# sourceMappingURL=basic-components.js.map